import { useMutation } from "@apollo/react-hooks";
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Tooltip,
} from "@material-ui/core";
import gql from "graphql-tag";
import React, { useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";

import logo from "../../assets/svgs/q-logo.svg";

const READY_FOR = gql`
  mutation ReadyFor($readyFor: [String]!) {
    readyFor(readyFor: $readyFor)
  }
`;

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 72;

const NAV_ITEMS = [
  {
    label: "Tenants",
    path: "/tenants",
    activePaths: ["/tenants", "/add-tenants", "/edit-tenants"],
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
      </svg>
    ),
  },
  {
    label: "Sources",
    path: "/managerSource",
    activePaths: ["/managerSource", "/SourceCreate", "/SourceEdit"],
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a10 10 0 1 0 10 10" /><path d="M12 2v10l6.5 3.5" />
        <circle cx="18" cy="18" r="3" /><path d="M20.5 15.5 22 14" />
      </svg>
    ),
  },
  {
    label: "API Keys",
    path: "/whatsapp/dialog360/add-api-key",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4" />
      </svg>
    ),
  },
];

function getInitials(name) {
  if (!name) return "U";
  return name.slice(0, 2).toUpperCase();
}

export default function MiniDrawer({ open, onToggle }) {
  const location = useLocation();
  const history = useHistory();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [readyFor] = useMutation(READY_FOR);

  const username = localStorage.getItem("user") || "User";
  const initials = getInitials(username);

  const handleConfirmLogout = () => {
    readyFor({ variables: { readyFor: ["not available"] } })
      .then(() => history.replace("/logout"))
      .catch(() => history.replace("/logout"));
    setLogoutOpen(false);
  };

  const isActive = ({ path, activePaths }) => {
    const checks = activePaths || [path];
    return checks.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + "/")
    );
  };

  const sidebarStyle = {
    width: open ? DRAWER_WIDTH : COLLAPSED_WIDTH,
    backgroundColor: "#111827",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    flexShrink: 0,
    transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    overflowX: "hidden",
    overflowY: "auto",
  };

  return (
    <>
      <div style={sidebarStyle}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: open ? "18px 16px" : "18px 17px",
            borderBottom: "1px solid #1f2937",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              backgroundColor: "#84cc16",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img
              src={logo}
              alt="OmniQ"
              style={{ width: 20, height: 20, filter: "brightness(0) invert(1)" }}
            />
          </div>
          {open && (
            <div style={{ overflow: "hidden" }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#fff",
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                OmniQ
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#6b7280",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Admin
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: "12px 8px", display: "flex", flexDirection: "column", gap: 2 }}>
          {open && (
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#4b5563",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "8px 8px 6px",
                whiteSpace: "nowrap",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Main
            </div>
          )}

          {NAV_ITEMS.map(({ label, path, activePaths, icon }) => {
            const active = isActive({ path, activePaths });
            return (
              <Tooltip key={path} title={open ? "" : label} placement="right" arrow>
                <Link
                  to={path}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: 8,
                    textDecoration: "none",
                    color: active ? "#fff" : "#9ca3af",
                    backgroundColor: active ? "#1f2937" : "transparent",
                    transition: "background-color 0.15s, color 0.15s",
                    position: "relative",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = "#1f2937";
                      e.currentTarget.style.color = "#e5e7eb";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "#9ca3af";
                    }
                  }}
                >
                  <span
                    style={{
                      color: active ? "#84cc16" : "currentColor",
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                      width: 20,
                      justifyContent: "center",
                    }}
                  >
                    {icon}
                  </span>
                  {open && (
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        flex: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      {label}
                    </span>
                  )}
                  {open && active && (
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "#84cc16",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Link>
              </Tooltip>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div style={{ borderTop: "1px solid #1f2937", padding: "8px", flexShrink: 0 }}>
          {/* User info */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px",
              borderRadius: 8,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: "#84cc16",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 600,
                color: "#fff",
                flexShrink: 0,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              {initials}
            </div>
            {open && (
              <div style={{ overflow: "hidden" }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#e5e7eb",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {username}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#6b7280",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Administrator
                </div>
              </div>
            )}
          </div>

          {/* Appearance */}
          <Tooltip title={open ? "" : "Appearance"} placement="right" arrow>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                borderRadius: 8,
                cursor: "pointer",
                color: "#9ca3af",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#1f2937";
                e.currentTarget.style.color = "#e5e7eb";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#9ca3af";
              }}
            >
              <span style={{ display: "flex", alignItems: "center", width: 20, justifyContent: "center", flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
                </svg>
              </span>
              {open && (
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: "'Inter', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  Appearance
                </span>
              )}
            </div>
          </Tooltip>

          {/* Sign Out */}
          <Tooltip title={open ? "" : "Sign Out"} placement="right" arrow>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                borderRadius: 8,
                cursor: "pointer",
                color: "#ef4444",
              }}
              onClick={() => setLogoutOpen(true)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#1f2937";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <span style={{ display: "flex", alignItems: "center", width: 20, justifyContent: "center", flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </span>
              {open && (
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: "'Inter', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  Sign Out
                </span>
              )}
            </div>
          </Tooltip>

          {/* Collapse */}
          <Tooltip title={open ? "" : "Expand sidebar"} placement="right" arrow>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                borderRadius: 8,
                cursor: "pointer",
                color: "#6b7280",
              }}
              onClick={onToggle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#1f2937";
                e.currentTarget.style.color = "#9ca3af";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#6b7280";
              }}
            >
              <span style={{ display: "flex", alignItems: "center", width: 20, justifyContent: "center", flexShrink: 0 }}>
                {open ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                )}
              </span>
              {open && (
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: "'Inter', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  Collapse
                </span>
              )}
            </div>
          </Tooltip>
        </div>
      </div>

      {/* Logout confirmation dialog */}
      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)}>
        <DialogTitle>Are you sure you want to sign out?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setLogoutOpen(false)} color="primary" autoFocus>
            Cancel
          </Button>
          <Button onClick={handleConfirmLogout} color="secondary">
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
